<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		Alert,
		Button,
		ColorWheel,
		Container,
		Slider,
		SliderV2,
		Text,
		Toggle,
		ToggleV2,
	} from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		get_device_component_index,
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
	} from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import SettingGuide from '../../SettingGuide.svelte';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';

	export let device;

	interface ButtonDoublepushConfig {
		key: string;
		enabled: boolean;
		brightness?: number;
		rgb?: number[];
		white?: number;
		ct?: number;
	}

	interface SettingRequestButtonPresets {
		id: number;
		config: {
			button_presets: {
				button_doublepush: ButtonDoublepushConfig | null;
			};
		};
	}

	let settings = readDeviceSettings(device.id);
	const buttonPresets = settings?.[get_gen_2_component_channel_structure(device)]?.button_presets;
	let enabled = buttonPresets?.button_doublepush != null;

	const minBrightness = 1;
	const maxBrightness = 100;
	const minWhite = 0;
	const maxWhite = 255;
	const mode = device?.mode;
	const supportsRgb = mode === 'rgb' || mode === 'rgbw';
	const supportsWhite = mode === 'rgbw';

	let loading = false;

	let cacheEnabled = enabled;

	let brightness = enabled ? buttonPresets?.button_doublepush?.brightness : 100;
	let cacheBrightness = brightness;

	const changeColor = (e) => {
		color = e.detail.color;
		rgb = joinColors(color);
	};

	const validateRgb = (_value) => {
		const max = 255;
		const min = 0;

		if (!isNaN(_value) && typeof _value !== 'number') {
			_value = Number(_value);
		}

		if (_value > max) {
			_value = max;
		}
		if (_value < min) {
			_value = min;
		}
		return +_value;
	};

	const toggleEnabled = (e) => {
		enabled = e.target.checked;
	};

	function splitColors(color) {
		return {
			r: validateRgb(color[0]),
			g: validateRgb(color[1]),
			b: validateRgb(color[2]),
		};
	}
	function joinColors(color) {
		return [validateRgb(color.r), validateRgb(color.g), validateRgb(color.b)];
	}

	let rgb = supportsRgb
		? (buttonPresets?.button_doublepush?.rgb ?? [100, 100, 100])
		: [100, 100, 100];
	let cacheRgb = [...rgb];
	let color = splitColors(rgb);

	let white = supportsWhite ? (buttonPresets?.button_doublepush?.white ?? 255) : 255;
	let cacheWhite = white;

	$: rgbDirty = supportsRgb && JSON.stringify(cacheRgb) !== JSON.stringify(rgb);
	$: whiteDirty = supportsWhite && cacheWhite !== white;
	$: isDirty = cacheBrightness !== brightness || cacheEnabled !== enabled || rgbDirty || whiteDirty;

	const sendReq = async () => {
		if (!isDirty) return;
		try {
			loading = true;

			const doublepush: ButtonDoublepushConfig | null = enabled
				? {
						key: 'button_doublepush',
						enabled: enabled,
						brightness: Number(brightness),
					}
				: null;

			if (doublepush && supportsRgb) {
				doublepush.rgb = rgb;
			}
			if (doublepush && supportsWhite) {
				doublepush.white = Number(white);
			}

			const params: SettingRequestButtonPresets = {
				id: get_device_component_index(device),
				config: {
					button_presets: {
						button_doublepush: doublepush,
					},
				},
			};

			const res: SettingRequest = await gen2Request(
				device,
				`${get_gen_2_component_from_category(device)}.SetConfig`,
				params,
			);

			const response = onSettingsResponse(res, device);
			if (response.isok) {
				cacheBrightness = brightness;
				cacheEnabled = enabled;
				if (supportsRgb) {
					cacheRgb = [...rgb];
				}
				if (supportsWhite) {
					cacheWhite = white;
				}
			} else {
				brightness = cacheBrightness;
				enabled = cacheEnabled;
				if (supportsRgb) {
					rgb = [...cacheRgb];
					color = splitColors(rgb);
				}
				if (supportsWhite) {
					white = cacheWhite;
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			loading = false;
		}
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: sendReq,
				loading,
				disabled: loading || !isDirty,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	$: brightnessA11y = {
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
	};

	$: whiteA11y = {
		ariaLabel: $format('settings_label_white_level', { default: 'White' }),
		ariaLabelValue: $format('current_white_value', {
			values: { value: white },
			default: `Current white value is ${white}`,
		}),
	};
</script>

{#if $showNewLayout}
	<!-- Dependent nested settings (SETTINGS.md): parent + children in one card; disable children when off. -->
	<div class="button-presets-plus-rgbw">
		<SettingGuide
			text={$format('settings_guide_double_press_preset', {
				default:
					'Set brightness (and color, if this profile supports it) when the wall button is double-pressed.',
			})}
		/>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_row_double_press_preset', { default: 'Double-press preset' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('description_button_presets', {
					default: 'Set various properties on action. If not set it will use the current values.',
				})}
			</Text>
			<ToggleV2 size="m" class="mb-s" bind:checked={enabled} id="toggle-0cadltf">
				{capFirstChar($format('enable', { default: 'Enable' }))}
			</ToggleV2>

			{#if !enabled}
				<TransitionWrapper type="fade">
					<Alert
						class="mb-s"
						type="info"
						heading={capFirstChar(
							$format('alert_info_heading_double_press_disabled', {
								default: 'Preset values disabled',
							}),
						)}
						isHeadingBold
						message={$format('alert_info_message_double_press_disabled', {
							default:
								'Brightness and color below have no effect while the double-press preset is off. Enable it to configure these values.',
						})}
					/>
				</TransitionWrapper>
			{/if}

			<div class="mb-l">
				<Text styleType="h2" class="mb-xs">
					{capFirstChar($format('collapse_title_brightness', { default: 'brightness' }))}
				</Text>
				<Text styleType="body-small" class="mb-s text-color-muted">
					{$format('settings_desc_double_press_brightness', {
						default: 'Brightness applied when the wall button is double-pressed.',
					})}
				</Text>
				<SliderV2
					id="slider-et97l2u"
					a11yProps={brightnessA11y}
					bind:value={brightness}
					on:value_change={(event) => {
						brightness = event.detail.value;
					}}
					disabled={!enabled}
					type="incremented"
					min={minBrightness}
					max={maxBrightness}
					withOverlay={false}
				/>
			</div>

			{#if supportsRgb}
				<div class="mb-l">
					<Text styleType="h2" class="mb-xs">
						{$format('settings_label_double_press_color', { default: 'Color' })}
					</Text>
					<Text styleType="body-small" class="mb-s text-color-muted">
						{$format('settings_desc_double_press_color', {
							default: 'RGB color applied when the wall button is double-pressed.',
						})}
					</Text>
					<ColorWheel
						id="colorwheel-i8k7vjm"
						a11yProps={{
							saveButtonAriaLabel: $format('label_save_color', { default: 'Save color' }),
							colorInputBAriaLabel: $format('label_color_input_b', { default: 'Color input B' }),
							colorInputGAriaLabel: $format('label_color_input_g', { default: 'Color input G' }),
							colorInputRAriaLabel: $format('label_color_input_r', { default: 'Color input R' }),
						}}
						valuesType="input"
						{color}
						disabled={loading || !enabled}
						hideSaveBtn
						on:color_change={changeColor}
					/>
				</div>
			{/if}

			{#if supportsWhite}
				<div class="mb-l">
					<Text styleType="h2" class="mb-xs">
						{$format('settings_label_white_level', { default: 'White' })}
					</Text>
					<Text styleType="body-small" class="mb-s text-color-muted">
						{$format('settings_desc_double_press_white', {
							default: 'White channel level applied when the wall button is double-pressed.',
						})}
					</Text>
					<SliderV2
						id="slider-plus-rgbw-white"
						a11yProps={whiteA11y}
						bind:value={white}
						on:value_change={(event) => {
							white = event.detail.value;
						}}
						disabled={!enabled}
						type="horizontal"
						min={minWhite}
						max={maxWhite}
						unit=""
						withOverlay={false}
					/>
				</div>
			{/if}
		</Container>
	</div>
{:else}
	<Text styleType="body-small" class="mb-m"
		>{$format('description_button_presets', {
			default: 'Set various properties on action. If not set it will use the current values.',
		})}</Text
	>

	<Toggle checked={enabled} on:click={toggleEnabled} size="m" class="mb-s" id="toggle-0cadltf">
		{$format('action_on_btn_double_push', {
			default: 'On button double push',
		})}
	</Toggle>

	<Slider
		id="slider-et97l2u"
		a11yProps={brightnessA11y}
		class="mb-s"
		bind:value={brightness}
		on:value_change={(event) => {
			brightness = event.detail.value;
		}}
		disabled={!enabled}
		title="Brightness"
		type="incremented"
		min={minBrightness}
		max={maxBrightness}
	/>

	{#if supportsRgb}
		<ColorWheel
			id="colorwheel-i8k7vjm"
			a11yProps={{
				saveButtonAriaLabel: $format('label_save_color', { default: 'Save color' }),
				colorInputBAriaLabel: $format('label_color_input_b', { default: 'Color input B' }),
				colorInputGAriaLabel: $format('label_color_input_g', { default: 'Color input G' }),
				colorInputRAriaLabel: $format('label_color_input_r', { default: 'Color input R' }),
			}}
			class="mb-xl"
			valuesType="input"
			{color}
			disabled={loading || !enabled}
			hideSaveBtn
			on:color_change={changeColor}
		/>
	{/if}

	{#if supportsWhite}
		<Slider
			id="slider-plus-rgbw-white"
			a11yProps={whiteA11y}
			class="mb-s"
			bind:value={white}
			on:value_change={(event) => {
				white = event.detail.value;
			}}
			disabled={!enabled}
			title={$format('settings_label_white_level', { default: 'White' })}
			type="simple"
			min={minWhite}
			max={maxWhite}
		/>
	{/if}

	<Button size="m" width="full" {loading} on:click={sendReq}>{$format('button_save')}</Button>
{/if}

<style>
	.button-presets-plus-rgbw {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
